/**
 * Utility functions for transforming object property names between camelCase and PascalCase
 */

/**
 * Converts a string from camelCase to PascalCase
 * @param str - The camelCase string to convert
 * @returns The PascalCase string
 */
export const toPascalCase = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Converts a string from PascalCase to camelCase
 * @param str - The PascalCase string to convert
 * @returns The camelCase string
 */
export const toCamelCase = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * Recursively transforms all object keys from camelCase to PascalCase
 * @param obj - The object to transform
 * @returns A new object with PascalCase keys
 */
export const transformKeysToPascalCase = <T = any>(obj: any): T => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => transformKeysToPascalCase(item)) as T;
    }

    if (typeof obj === 'object' && obj.constructor === Object) {
        const transformed: any = {};

        for (const [key, value] of Object.entries(obj)) {
            const pascalKey = toPascalCase(key);
            transformed[pascalKey] = transformKeysToPascalCase(value);
        }

        return transformed as T;
    }

    return obj;
};

/**
 * Recursively transforms all object keys from PascalCase to camelCase
 * @param obj - The object to transform
 * @returns A new object with camelCase keys
 */
export const transformKeysToCamelCase = <T = any>(obj: any): T => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => transformKeysToCamelCase(item)) as T;
    }

    if (typeof obj === 'object' && obj.constructor === Object) {
        const transformed: any = {};

        for (const [key, value] of Object.entries(obj)) {
            const camelKey = toCamelCase(key);
            transformed[camelKey] = transformKeysToCamelCase(value);
        }

        return transformed as T;
    }

    return obj;
};

/**
 * Type-safe wrapper for API responses that need key transformation
 * @param apiResponse - The API response with camelCase keys
 * @returns The transformed object with PascalCase keys
 */
export const transformApiResponse = <T>(apiResponse: any): T => {
    return transformKeysToPascalCase<T>(apiResponse);
};

/**
 * Type-safe wrapper for API requests that need key transformation
 * @param requestData - The request data with PascalCase keys
 * @returns The transformed object with camelCase keys
 */
export const transformApiRequest = <T>(requestData: any): T => {
    return transformKeysToCamelCase<T>(requestData);
};
